import React, { useState, useEffect, useCallback } from 'react';
import { useSnackbar } from 'notistack';

import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Modal from '@mui/material/Modal';
import IconButton from '@mui/material/IconButton';
import CircularProgress from '@mui/material/CircularProgress';
import CloseIcon from '@mui/icons-material/Close';
import { Box } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import InputMask from 'react-input-mask';
import { useUser } from '../../Hooks/useUser';
import axios from "../../api/axios";

const Contacts = (props) => {
  const { enqueueSnackbar } = useSnackbar();
  const { user: contextUser } = useUser();
  const user = props.User ?? contextUser;

  const [bomEmergencyContact, setBomEmergencyContact] = useState({
    BPEmergencyContactsID: 0,
    BP_UserGUID: user?.UserGUID,
    BP_EC_FullName: '',
    BP_EC_Relationship: '',
    BP_EC_Email: '',
    BP_EC_Phone: '',
  });

  const [emergencyContacts, setEmergencyContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [errorFields, setErrorFields] = useState({});

  const [open, setOpen] = useState(false);
  const handleClose = () => setOpen(false);
  const [editMode, setEditMode] = useState(false);

  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [contactToDelete, setContactToDelete] = useState(null);
  const handleDeleteConfirmClose = () => {
    setDeleteConfirmOpen(false);
    setContactToDelete(null);
  };

  const resetContact = useCallback(() => {
    setBomEmergencyContact({
      BPEmergencyContactsID: 0,
      BP_UserGUID: user?.UserGUID,
      BP_EC_FullName: '',
      BP_EC_Relationship: '',
      BP_EC_Email: '',
      BP_EC_Phone: '',
    });
  }, [user?.UserGUID]);

  const handleAdd = () => {
    setEditMode(false);
    setErrorFields({});
    resetContact();
    setOpen(true);
  };

  const handleEdit = (obj, params) => {
    setEditMode(true);
    setErrorFields({});
    setOpen(true);
  };

  const handleDeleteClick = (ob, params) => {
    setContactToDelete(params.row);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteConfirm = () => {
    if (!contactToDelete) return;

    axios.delete(`/api/emergency-contacts/${contactToDelete["BPEmergencyContactsID"]}`)
      .then(() => {
        enqueueSnackbar("Emergency Contact Deleted Successfully");
        getData();
        handleDeleteConfirmClose();
      })
      .catch((err) => {
        console.error(err);
        enqueueSnackbar(err?.response?.data?.error || "Emergency Contact Delete Failed", { variant: 'error' });
        handleDeleteConfirmClose();
      });
  };

  function handleFieldChange(input) {
    const target = input.target;
    const value = target.value;
    const name = target.id;
    setBomEmergencyContact({ ...bomEmergencyContact, [name]: value });
    if (errorFields[name]) {
      setErrorFields({ ...errorFields, [name]: false });
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault();

    const errors = {};
    if (!bomEmergencyContact.BP_EC_FullName?.trim()) {
      errors.BP_EC_FullName = true;
      enqueueSnackbar("Please enter contact name", { variant: 'error' });
    }
    if (!bomEmergencyContact.BP_EC_Email?.trim()) {
      errors.BP_EC_Email = true;
      enqueueSnackbar("Please enter contact email", { variant: 'error' });
    }
    if (!bomEmergencyContact.BP_EC_Phone?.trim()) {
      errors.BP_EC_Phone = true;
      enqueueSnackbar("Please enter contact phone number", { variant: 'error' });
    }

    if (Object.keys(errors).length > 0) {
      setErrorFields(errors);
      return;
    }
    setErrorFields({});
    setSaving(true);

    // Build the payload locally instead of relying on state that hasn't
    // re-rendered yet — setBomEmergencyContact here wouldn't be visible
    // to the axios call on the next line.
    const payload = { ...bomEmergencyContact, BP_UserGUID: user.UserGUID };
    setBomEmergencyContact(payload);

    const request = editMode
      ? axios.put('/api/emergency-contacts', payload)
      : axios.post('/api/emergency-contacts', payload);

    try {
      await request;
      enqueueSnackbar(editMode ? "Emergency Contact Updated" : "Emergency Contact Saved");
      getData();
      handleClose();
      resetContact();
    } catch (ex) {
      console.error(ex);
      enqueueSnackbar(
        ex?.response?.data?.error ||
        (editMode ? "Emergency Contact Update Failed" : "Emergency Contact Save Failed"),
        { variant: 'error' }
      );
    } finally {
      setSaving(false);
    }
  };

  const modalStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 700,
    bgcolor: 'background.paper',
    border: '2px solid #000',
    boxShadow: 24,
    p: 4,
  };

  const columns = [
    { field: 'BP_EC_FullName', headerName: 'Name', flex: 1, minWidth: 150 },
    { field: 'BP_EC_Relationship', headerName: 'Relationship', flex: 1, minWidth: 150 },
    { field: 'BP_EC_Email', headerName: 'Email', flex: 1.5, minWidth: 200 },
    { field: 'BP_EC_Phone', headerName: 'Phone', flex: 1, minWidth: 150 },
    {
      field: "Actions", flex: 1.5, minWidth: 200,
      renderCell: (cellValues) => (
        <div className="d-flex gap-2">
          <Button
            variant="contained"
            color="primary"
            onClick={(event) => {
              setBomEmergencyContact(cellValues.row);
              handleEdit(event, cellValues);
            }}
          >
            Edit
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={(event) => handleDeleteClick(event, cellValues)}
          >
            Delete
          </Button>
        </div>
      )
    },
  ];

  const getData = useCallback(() => {
    if (!user?.UserGUID) return;

    setLoading(true);
    axios.get(`/api/emergency-contacts/${user.UserGUID}`)
      .then((response) => {
        setEmergencyContacts(response.data);
      })
      .catch((error) => {
        console.error(error);
        enqueueSnackbar('Failed to load emergency contacts', { variant: 'error' });
      })
      .finally(() => setLoading(false));
  }, [user?.UserGUID, enqueueSnackbar]);

  useEffect(() => {
    getData();
  }, [getData]);

  useEffect(() => {
    setBomEmergencyContact((prev) => ({ ...prev, BP_UserGUID: user?.UserGUID }));
  }, [user?.UserGUID]);

  return (
    <div id="tab5">
      <div className="row px-5 pt-5">
        <div className="col-md-12">
          <DataGrid
            flexGrow
            autoHeight={true}
            autoPageSize={true}
            pageSize={10}
            columns={columns}
            rows={emergencyContacts}
            getRowId={(row) => row.BPEmergencyContactsID}
            disableMultipleSelection={true}
            disableSelectionOnClick={true}
            loading={loading}
          />
        </div>

        <div className="col-md-12">
          <a data-toggle="collapse" onClick={handleAdd} role="button" aria-expanded="false" aria-controls="add-contact" className="button-default collapsed">Add Contact</a>
          <div className="collapse" id="add-contact">
            <Modal open={open} onClose={handleClose} aria-labelledby='title' aria-describedby=''>
              <Box sx={modalStyle}>
                <IconButton
                  aria-label="close"
                  onClick={handleClose}
                  sx={{ position: 'absolute', right: 8, top: 8, color: (theme) => theme.palette.grey[500] }}
                >
                  <CloseIcon />
                </IconButton>
                <Typography id="title" variant='h5'>Emergency Contacts</Typography>
                <div className="row pt-3">
                  <div className="col-md-6">
                    <div className="form-group">
                      <label className="required-caption">Contact Name<span className="required-field">*</span></label>
                      <input type="text" id='BP_EC_FullName' value={bomEmergencyContact.BP_EC_FullName} maxLength="200" className={`form-control ${errorFields.BP_EC_FullName ? 'error-field' : ''}`} placeholder="Contact Name" onChange={handleFieldChange} />
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="form-group">
                      <label className="required-caption">Contact Relationship</label>
                      <input type="text" id='BP_EC_Relationship' value={bomEmergencyContact.BP_EC_Relationship} maxLength="200" className="form-control" placeholder="Contact Relationship" onChange={handleFieldChange} />
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="form-group">
                      <label className="required-caption">Contact Email<span className="required-field">*</span></label>
                      <input type="text" id='BP_EC_Email' value={bomEmergencyContact.BP_EC_Email} maxLength="200" className={`form-control ${errorFields.BP_EC_Email ? 'error-field' : ''}`} placeholder="Contact Email" onChange={handleFieldChange} />
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="form-group">
                      <label className="required-caption">Contact Phone Number<span className="required-field">*</span></label>
                      <InputMask
                        mask="(999) 999-9999"
                        value={bomEmergencyContact.BP_EC_Phone || ''}
                        onChange={handleFieldChange}
                      >
                        {(inputProps) => (
                          <input
                            {...inputProps}
                            id='BP_EC_Phone'
                            type="text"
                            className={`form-control ${errorFields.BP_EC_Phone ? 'error-field' : ''}`}
                            placeholder="(123) 456-7890"
                          />
                        )}
                      </InputMask>
                    </div>
                  </div>
                  <div className="col-md-6 text-white py-1 d-flex gap-2 mt-3">
                    <a data-toggle="collapse" role="button" aria-expanded="false" onClick={handleSubmit} aria-controls="save-skills" className="button-default button-green collapsed" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', minHeight: '38px', padding: '8px 20px', opacity: saving ? 0.7 : 1, pointerEvents: saving ? 'none' : 'auto' }}>
                      {saving && <CircularProgress size={14} style={{ color: 'white' }} />}
                      Save
                    </a>
                    <a data-toggle="collapse" onClick={handleClose} role="button" aria-expanded="false" aria-controls="cancel-skills" className="button-default button-gray collapsed">Cancel</a>
                  </div>
                  <div className="col-md-6 text-white py-1 d-flex gap-2 justify-content-end mt-3">
                    {editMode && <a data-toggle="collapse" role="button" aria-expanded="false" aria-controls="delete-skills" className="button-default button-red collapsed">Delete</a>}
                  </div>
                </div>
              </Box>
            </Modal>
          </div>

          <Modal open={deleteConfirmOpen} onClose={handleDeleteConfirmClose} aria-labelledby='delete-title' aria-describedby='delete-description'>
            <Box sx={modalStyle}>
              <IconButton
                aria-label="close"
                onClick={handleDeleteConfirmClose}
                sx={{ position: 'absolute', right: 8, top: 8, color: (theme) => theme.palette.grey[500] }}
              >
                <CloseIcon />
              </IconButton>
              <Typography id="delete-title" variant='h5' sx={{ mb: 2 }}>Confirm Delete</Typography>
              <Typography id="delete-description" variant='body1' sx={{ mb: 4 }}>
                Are you sure you want to delete this entry? This action cannot be undone.
              </Typography>
              <div className="row">
                <div className="col-md-12 text-white py-1 d-flex gap-2">
                  <a role="button" onClick={handleDeleteConfirm} className="button-default button-red collapsed">Delete</a>
                  <a role="button" onClick={handleDeleteConfirmClose} className="button-default button-gray collapsed">Cancel</a>
                </div>
              </div>
            </Box>
          </Modal>
        </div>
      </div>
    </div>
  );
};

export default Contacts;